import React, { useEffect, useMemo, useState } from 'react'
import {
  Box,
  Divider,
  Grid,
  IconButton,
  Input,
  InputLabel,
  Link,
  makeStyles,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography
} from '@material-ui/core'
import { useTranslation } from 'react-i18next'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import moment from 'moment'
import { Autocomplete } from '@mui/material'
import ReactQuill from 'react-quill'
import 'react-quill/dist/quill.snow.css'
import { useDispatch, useSelector } from 'react-redux'
import { proposalsActions } from '../../store/proposals'
import {
  createProposalAttachment,
  deleteProposalAttachment,
  updateProposal
} from '../../services/ApiService'
import { downloadURI } from '../../lib/Global'
import { editableProposalStatus } from '../../lib/Constants'
import LoadingBox from '../form/LoadingBox'
import { DetailedInfo } from '../workorders/DetailedInfo'
import ReactGA from 'react-ga4'
import DragAndDropUploader from '../form/DragAndDropUploader'

const useStyles = makeStyles(theme => ({
  headerContainer: {
    flexGrow: 1,
    margin: '0px 20px 20px',
    paddingTop: '8px',
    color: theme.colors.text
  },
  proposalsContainer: {
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    height: '100%'
  },
  generalInfo: {
    paddingBottom: '40px'
  },
  leftColumn: {
    display: 'flex',
    flex: '1',
    flexDirection: 'column',
    paddingRight: '10px'
  },
  subContainer: {
    display: 'flex',
    flexDirection: 'row',
    whiteSpace: 'nowrap'
  },
  inputElement: {
    padding: '5px',
    flex: '1'
  },
  rightColumn: {
    display: 'flex',
    flex: '1',
    flexDirection: 'column',
    paddingLeft: '10px'
  },
  proposal_id: {
    fontSize: '18px',
    fontWeight: '700',
    display: 'flex',
    alignItems: 'center'
  },
  proposal_status: {
    fontSize: '32px',
    fontWeight: '700'
  },
  draft: {
    color: theme.colors.proposalColors.draft
  },
  approved: {
    color: theme.colors.proposalColors.approved
  },
  closed: {
    color: theme.colors.statusBarColors.dark_grey_solid
  },
  declined: {
    color: theme.colors.proposalColors.rejected
  },
  in_review: {
    color: theme.colors.statusBarColors.in_review
  },
  pending_decision: {
    color: theme.colors.statusBarColors.pending_decision
  },
  proposal_grey_label: {
    fontSize: '12px',
    fontWeight: '500',
    color: theme.colors.proposalColors.grey_text
  },
  proposal_placeholder: {
    fontSize: '12px',
    color: theme.colors.proposalColors.grey_text
  },
  proposal_medium_label: {
    fontSize: '12px',
    fontWeight: '500',
    color: theme.colors.disable_text,
    paddingLeft: '5px'
  },
  bootstrapFormLabel: {
    fontSize: '16px',
    fontWeight: '500',
    height: '20px',
    color: theme.colors.proposalColors.labels_grey
  },
  inputRoot: {
    borderRadius: '8px',
    width: '100%',
    borderColor: theme.colors.proposalColors.blue_border,
    height: '32px',
    fontSize: '12px',
    color: theme.colors.proposalColors.grey_text,
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px',
      height: '32px',
      fontSize: '12px',
      color: theme.colors.proposalColors.grey_text,
      '& fieldset': {
        borderColor: theme.colors.proposalColors.blue_border
      },
      '&:hover fieldset': {
        borderColor: theme.colors.proposalColors.blue_border
      },
      '&.Mui-focused fieldset': {
        borderColor: theme.colors.proposalColors.blue_border
      }
    },
    '& .MuiInputBase-input': {
      padding: '0px 10px'
    },
    '& .MuiAutocomplete-input': {
      padding: '0px !important'
    }
  },
  select: {
    borderRadius: '8px',
    fontSize: '12px',
    borderColor: theme.colors.proposalColors.blue_border,
    height: '32px',
    color: theme.colors.proposalColors.grey_text,
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.colors.proposalColors.blue_border,
      borderRadius: '8px'
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.colors.proposalColors.blue_border + ' !important'
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.colors.proposalColors.blue_border + ' !important'
    },
    '& .MuiInputBase-input': {
      fontSize: '12px'
    },
    '& .MuiOutlinedInput-input': {
      padding: '0px 10px'
    }
  },
  multilineField: {
    borderRadius: '8px',
    width: '100%',
    borderColor: theme.colors.proposalColors.blue_border,
    fontSize: '12px',
    color: theme.colors.proposalColors.grey_text,
    marginBottom: '20px',
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px',
      fontSize: '12px',
      color: theme.colors.proposalColors.grey_text,
      '& fieldset': {
        borderColor: theme.colors.proposalColors.blue_border
      },
      '&:hover fieldset': {
        borderColor: theme.colors.proposalColors.blue_border
      },
      '&.Mui-focused fieldset': {
        borderColor: theme.colors.proposalColors.blue_border
      }
    },
    '& .MuiInputBase-input': {
      padding: '0px 10px'
    }
  },
  displayOnly: {
    border: '1px solid ' + theme.colors.proposalColors.blue_border,
    fontSize: '12px',
    color: theme.colors.text,
    height: '32px',
    padding: '0px 10px',
    borderRadius: '8px'
  },
  innerSelectItem: {
    fontSize: '12px',
    width: '140px'
  },
  autocompleteListbox: {
    borderRadius: 8,
    fontSize: '12px',
    maxHeight: 150
  },
  autocompleteOption: {
    fontSize: '12px'
  },
  requiredAsterisk: {
    color: theme.colors.errorText
  },
  propIdInput: {
    '& .MuiInputBase-input': {
      maxWidth: '90px',
      fontSize: '18px',
      color: theme.colors.proposalColors.blue_text + ' !important'
    }
  },
  blueText: {
    color: theme.colors.proposalColors.blue_text + ' !important',
    textDecoration: 'underline'
  },
  icons: {
    color: theme.colors.proposalColors.blue_text + ' !important'
  },
  attachInput: {
    display: 'none'
  },
  loadingBox: {
    display: 'flex',
    textAlign: 'right',
    marginLeft: '3px'
  },
  loadingFiles: {
    marginLeft: 'auto',
    marginTop: '3px',
    color: theme.colors.iconBlue
  },
  attachmentBox: {
    cursor: 'pointer',
    display: 'flex',
    textAlign: 'right'
  },
  boxAttahcment: {
    display: 'flex',
    textAlign: 'right'
  },
  attachButton: {
    cursor: 'pointer',
    border: 'none',
    backgroundColor: theme.colors.white,
    color: theme.colors.iconBlue,
    width: '15px',
    marginLeft: 'auto'
  },
  attachTypo: {
    textAlign: 'initial',
    color: theme.colors.iconBlue,
    fontSize: '10px',
    fontWeight: '500',
    margin: 'auto 0px auto 10px',
    display: '-webkit-box'
  },
  attachTypoOld: {
    cursor: 'pointer',
    textAlign: 'initial',
    color: theme.colors.iconBlue,
    fontSize: '10px',
    fontWeight: '500',
    margin: 'auto 0px auto 10px',
    whiteSpace: 'nowrap',
    '&:hover': {
      textDecoration: 'underline'
    }
  },
  endingAttach: {
    textAlign: 'initial',
    color: theme.colors.iconBlue,
    fontSize: '10px',
    fontWeight: '500',
    margin: 'auto 0px auto 0px'
  },
  endingAttachOld: {
    cursor: 'pointer',
    textAlign: 'initial',
    color: theme.colors.iconBlue,
    fontSize: '10px',
    fontWeight: '500',
    margin: 'auto 0px auto 0px',
    '&:hover': {
      textDecoration: 'underline'
    }
  },
  deleteIcon: {
    cursor: 'pointer',
    border: 'none',
    backgroundColor: theme.colors.white,
    color: theme.colors.grey,
    width: '15px'
  },
  listAttachments: {
    display: 'flex',
    textAlign: 'right',
    flexWrap: 'wrap',
    justifyContent: 'flex-end'
  },
  quillWrapper: {
    display: 'flex',
    flexDirection: 'column'
  },
  quillToolbar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '5px',
    borderLeft: `1px solid ${theme.colors.proposalColors.blue_border} !important`,
    borderRight: `1px solid ${theme.colors.proposalColors.blue_border} !important`,
    borderBottom: `1px solid ${theme.colors.proposalColors.blue_border} !important`,
    borderTop: 'none !important',
    borderBottomLeftRadius: '8px',
    borderBottomRightRadius: '8px'
  },
  quillContainer: {
    '& .ql-container': {
      borderLeft: `1px solid ${theme.colors.proposalColors.blue_border}`,
      borderRight: `1px solid ${theme.colors.proposalColors.blue_border}`,
      borderTop: `1px solid ${theme.colors.proposalColors.blue_border}`,
      borderBottom: 'none',
      borderTopLeftRadius: '8px',
      borderTopRightRadius: '8px'
    },
    '& .ql-editor': {
      minHeight: '150px',
      padding: '5px 8px'
    }
  },
  customButtonContainer: {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    marginLeft: 'auto',
    color: theme.colors.iconBlue,
    textTransform: 'none',
    fontSize: '12px',
    fontWeight: '400',
    width: 'fit-content !important',
    border: '1px solid ' + theme.colors.profile.border_input,
    borderRadius: '8px',
    padding: '8px 12px'
  },
  customButton: {
    display: 'flex',
    alignItems: 'center'
  },
  emptyContainer: {
    marginLeft: 'auto'
  },
  customIcon: {
    marginRight: '5px'
  },
  disabled: {
    pointerEvents: 'none',
    '& .ql-toolbar': {
      display: 'none'
    },
    '& .ql-editor': {
      borderRadius: '8px',
      borderColor: theme.colors.proposalColors.disabled_grey
    }
  },
  errorMessage: {
    color: theme.colors.proposalColors.error_message,
    padding: '4px 0px',
    fontSize: '12px'
  },
  loadingBox_1: {
    width: '250px',
    height: '20px',
    margin: '11px 0px'
  },
  workOrderContainer: {
    padding: '20px 0px'
  },
  workorderNotes: {
    borderRadius: '8px',
    width: '100%',
    borderColor: theme.colors.proposalColors.blue_border,
    '& .MuiInputBase-input': {
      fontSize: '12px',
      color: theme.colors.proposalColors.grey_text
    },
    '& .MuiOutlinedInput-multiline': {
      padding: '5px 10px 10px'
    }
  },
  woInfo: {
    display: 'flex',
    flexDirection: 'row',
    padding: '10px 0px 15px'
  },
  tooltip: {
    backgroundColor: theme.colors.dark_blue,
    fontSize: '12px',
    color: theme.colors.white,
    fontWeight: '400'
  },
  noPaddingIconButton: {
    padding: '0px 10px',
    minWidth: 'auto'
  },
  linkWoNumber: {
    color: theme.colors.text,
    fonstSize: '12px'
  },
  headerRight: {
    textAlign: 'right',
    display: 'flex',
    justifyContent: 'flex-end'
  },
  attachmentContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'end'
  },
  descriptionContainer: {
    paddingLeft: '5px',
    paddingTop: '5px'
  },
  icon: {
    fontSize: 24
  }
}))
function ProposalHeader(props) {
  const {
    data,
    handleChangeData,
    editionDisabled,
    locationList,
    userList,
    priorityList,
    tradeList,
    proposalPrefix,
    proposalNumberId,
    attachments,
    setAttachments,
    setData,
    sfUserList
  } = props
  const { t } = useTranslation()
  const classes = useStyles()
  const dispatch = useDispatch()
  const loadingFiles = useSelector(state => state.proposals.loadingFiles)
  const [editPropId, setEditPropId] = useState(false)
  const [description, setDescription] = useState('')
  const [originalProposalId, setOriginalProposalId] = useState('')
  const [selectedPriority, setSelectedPriority] = useState('')
  const [selectedTrade, setSelectedTrade] = useState(null)
  const [submittedDate, setSubmittedDate] = useState()
  const [selectedWo, setSelectedWo] = useState(null)
  const [editionInProgress, setEditionInProgress] = useState(false)
  const [errorMessage, setErrorMessage] = useState({
    status: false,
    message: ''
  })

  useEffect(() => {
    setDescription(data?.description ?? '')
    setSelectedPriority(data?.priority ?? '')
    const tempDate = data?.history?.find(item => item.action === 'submit')
    setSubmittedDate(tempDate?.timestamp ?? false)
  }, [data])

  useEffect(() => {
    setSelectedTrade(data?.trade ?? '')
  }, [tradeList])

  const handleInputChange = (field, value) => {
    if (field === 'location') {
      handleChangeData(field, {
        id: value.id,
        name: value.label,
        address: value.address,
        client_id: value.client_id,
        client_name: value.client_name
      })
    } else {
      handleChangeData(field, value)
    }
  }

  const handleDescriptionChange = value => {
    setDescription(value)
    handleChangeData('description', value)
  }

  const handleProposalNumberChange = value => {
    setEditionInProgress(true)
    const proposalId = proposalPrefix + value
    handleChangeData('proposal_id', proposalId)
  }

  const toogleProposalIdEdition = () => {
    if (!editPropId) {
      setOriginalProposalId(data?.proposal_id ?? '')
      setEditPropId(!editPropId)
      setErrorMessage({ status: false, message: '' })
    } else {
      const newId = proposalPrefix + data?.proposal_id.slice(3).padStart(7, '0')
      handleChangeData('proposal_id', newId)
      handleUpdateProposalIdNumber(newId)
    }
  }
  const handleUpdateProposalIdNumber = async value => {
    const updateData = {
      ...data,
      id: data.id,
      subcontractor: data?.subcontractor,
      status: data.status,
      proposal_id: value
    }
    try {
      // prevent empty total amount
      if (!data.total_amount) {
        delete updateData.total_amount
      }
      const response = await updateProposal(data.id, updateData, false, true)
      setErrorMessage({ status: false, message: '' })
      setData(response?.content)
      setEditPropId(false)
    } catch (error) {
      if (error?.message === 'This ID has already been used') {
        setErrorMessage({
          status: true,
          message: t('proposals.error.id_in_use')
        })
      } else {
        setErrorMessage({
          status: true,
          message: t('proposals.error.update_error')
        })
      }
    }
  }
  const cancelProposalIdEdition = () => {
    handleChangeData('proposal_id', originalProposalId)
    setEditPropId(false)
    setErrorMessage({ status: false, message: '' })
  }

  const onFileChange = files => {
    const toInsert = []
    files.forEach(element => {
      const duplicate = attachments.find(
        attachment => attachment.title === element.name
      )
      if (!duplicate) toInsert.push(element)
    })
    if (toInsert.length > 0) {
      saveAttachmentsDraft(toInsert)
    }
  }
  const saveAttachmentsDraft = async toInsert => {
    dispatch(proposalsActions.setLoadingFiles(true))
    const validFiles = []
    for (const attachment of toInsert) {
      validFiles.push({
        proposal_id: data.id,
        title: attachment.name,
        content: attachment,
        size: attachment.size,
        type: attachment.name.split('.').pop()
      })
    }
    const attachmentsRes = await createProposalAttachment(validFiles)
    setAttachments(prevData => [...prevData, ...attachmentsRes])
    dispatch(proposalsActions.setLoadingFiles(false))
  }

  const deleteValidFile = async actualFile => {
    dispatch(proposalsActions.setLoadingFiles(true))
    await deleteProposalAttachment(data.id, actualFile.external_id)
    if (attachments.length === 1) {
      setAttachments([])
    } else {
      setAttachments(
        attachments.filter(
          (element, ind) => element.external_id !== actualFile.external_id
        )
      )
    }
    dispatch(proposalsActions.setLoadingFiles(false))
  }

  const [reload, setReload] = useState(false)
  const handleCloseWoPanel = () => {
    setSelectedWo(null)
  }

  const CustomTextEditor = ({
    description,
    editionDisabled,
    handleDescriptionChange
  }) => {
    const [localData, setLocalData] = useState(description)

    useState(() => {
      setLocalData(description)
    }, [description])

    const handleBlurEvent = () => {
      if (localData !== description) {
        handleDescriptionChange(localData)
      }
    }

    const handleCopyWoDescription = () => {
      if (editionDisabled) return
      setLocalData(data?.workorder?.general_notes)
    }

    const modules = useMemo(
      () => ({
        toolbar: {
          container: '#custom-toolbar'
        }
      }),
      []
    )
    return (
      <Box className={classes.quillWrapper}>
        <InputLabel
          shrink
          className={classes.bootstrapFormLabel}
          htmlFor="descriptionb-input"
        >
          {t('proposals.column_names.description')}
        </InputLabel>

        {/* Quill editor */}
        <ReactQuill
          id="descriptionb-input"
          value={localData}
          readOnly={editionDisabled}
          onChange={setLocalData}
          onBlur={handleBlurEvent}
          placeholder={t('proposals.placeholder.description')}
          modules={modules}
          formats={['bold', 'italic', 'underline', 'list', 'bullet', 'indent']}
          className={`${classes.quillContainer} ${
            editionDisabled ? classes.disabled : ''
          }`}
        />

        {/* Toolbar format buttons */}
        <div id="custom-toolbar" className={classes.quillToolbar}>
          <span className="ql-formats">
            <button className="ql-bold"></button>
            <button className="ql-italic"></button>
            <button className="ql-underline"></button>
          </span>
          <span className="ql-formats">
            <button className="ql-list" value="bullet"></button>
            <button className="ql-list" value="ordered"></button>
            <button className="ql-indent" value="+1"></button>
            <button className="ql-indent" value="-1"></button>
          </span>
          <span className="ql-formats">
            <button className="ql-clean"></button>
          </span>
          {/* custom paste button */}
          <Box
            className={
              data?.workorder
                ? classes.customButtonContainer
                : classes.emptyContainer
            }
            onClick={handleCopyWoDescription}
          >
            {data?.workorder && (
              <span className={classes.customButton}>
                <FontAwesomeIcon
                  icon="fa-solid fa-paste"
                  className={classes.customIcon}
                />
                <span>{t('proposals.actions.paste_description')}</span>
                <Tooltip
                  title={t('proposals.tooltips.copy')}
                  arrow
                  placement="top-end"
                  classes={{ tooltip: classes.tooltip }}
                >
                  <IconButton size="medium">
                    <FontAwesomeIcon
                      icon="fa-regular fa-circle-info"
                      fontSize="small"
                    />
                  </IconButton>
                </Tooltip>
              </span>
            )}
          </Box>
        </div>
      </Box>
    )
  }

  return (
    <Box classes={{ root: classes.headerContainer }}>
      <DetailedInfo
        workOrder={selectedWo}
        handleClosePanel={handleCloseWoPanel}
        viewMode
        reload={reload}
        setReload={setReload}
      />
      <Grid container className={classes.generalInfo}>
        <Grid item xs={8}>
          {!proposalNumberId && !editionInProgress ? (
            <LoadingBox className={classes.loadingBox_1} />
          ) : (
            <Typography classes={{ root: classes.proposal_id }}>
              <span>{'#' + proposalPrefix}</span>
              {editPropId ? (
                <Input
                  className={classes.propIdInput}
                  onFocus={e => e.target.select()}
                  value={proposalNumberId}
                  onChange={e => handleProposalNumberChange(e.target.value)}
                  inputProps={{ maxLength: 6 }}
                />
              ) : (
                <div className={editionDisabled ? '' : classes.blueText}>
                  {proposalNumberId}
                </div>
              )}

              {!editionDisabled && (
                <IconButton size="large">
                  <FontAwesomeIcon
                    className={classes.icons}
                    icon={editPropId ? 'fa-solid fa-check' : 'fa-solid fa-pen'}
                    fontSize="large"
                    onClick={toogleProposalIdEdition}
                  />
                </IconButton>
              )}
              {editPropId && (
                <IconButton size="large">
                  <FontAwesomeIcon
                    className={classes.icons}
                    icon={'fa-solid fa-times'}
                    fontSize="large"
                    onClick={cancelProposalIdEdition}
                  />
                </IconButton>
              )}
              <Tooltip
                title={t('proposals.tooltips.proposal_id')}
                arrow
                placement="top-end"
                classes={{ tooltip: classes.tooltip }}
              >
                <IconButton size="medium">
                  <FontAwesomeIcon
                    icon="fa-regular fa-circle-info"
                    fontSize="large"
                  />
                </IconButton>
              </Tooltip>
            </Typography>
          )}

          {errorMessage.status && (
            <Typography className={classes.errorMessage}>
              <FontAwesomeIcon icon="fa-solid fa-ban" />
              {' ' + errorMessage.message}
            </Typography>
          )}
          {!proposalNumberId && !editionInProgress ? (
            <LoadingBox className={classes.loadingBox_1} />
          ) : (
            <Typography classes={{ root: classes.proposal_status }}>
              {t('proposals.labels.proposal') + ': '}
              <span className={classes[data?.status]}>
                {t('proposals.status.' + data?.status)}
              </span>
              <Tooltip
                title={t('proposals.tooltips.status')}
                arrow
                placement="top-end"
                classes={{ tooltip: classes.tooltip }}
              >
                <IconButton size="large">
                  <FontAwesomeIcon
                    icon="fa-regular fa-circle-info"
                    fontSize="large"
                  />
                </IconButton>
              </Tooltip>
            </Typography>
          )}

          {!proposalNumberId && !editionInProgress ? (
            <LoadingBox className={classes.loadingBox_1} />
          ) : (
            <Box display={'flex'} flexDirection={'row'}>
              <Typography classes={{ root: classes.proposal_grey_label }}>
                {t('proposals.labels.submit_date') + ':  '}
              </Typography>
              <Typography classes={{ root: classes.proposal_medium_label }}>
                {submittedDate
                  ? moment(new Date(submittedDate * 1000)).format(
                      t('general.format.dot_date')
                    )
                  : 'Not submitted'}
              </Typography>
            </Box>
          )}
          {data?.workorder?.customer_po && (
            <Typography classes={{ root: classes.proposal_grey_label }}>
              <span>{t('invoices.wo_number') + ': '}</span>
              <Link
                className={classes.linkWoNumber}
                underline="always"
                onClick={event => {
                  event.stopPropagation()
                  ReactGA.event({
                    category: 'show',
                    action: 'show_work_order_detail'
                  })
                  setSelectedWo({
                    ...data?.workorder
                  })
                }}
              >
                {data?.workorder?.customer_po}
              </Link>
            </Typography>
          )}
        </Grid>
        <Grid item xs={4} className={classes.headerRight}>
          <Box className={classes.attachmentContainer}>
            <Typography classes={{ root: classes.proposal_grey_label }}>
              {t('proposals.labels.service_provided') + ': '}
            </Typography>
            <Typography classes={{ root: classes.proposal_medium_label }}>
              {data?.management_company?.name}
            </Typography>
            <Typography classes={{ root: classes.proposal_medium_label }}>
              {data?.management_company?.address?.address1}
            </Typography>
            <Typography classes={{ root: classes.proposal_medium_label }}>
              {data?.management_company?.address?.city},{' '}
              {data?.management_company?.address?.state},{' '}
              {data?.management_company?.address?.zipCode}
            </Typography>
            {(editableProposalStatus.includes(data?.status) ||
              attachments?.length > 0) && (
              <DragAndDropUploader
                attachments={attachments}
                onFilesSelect={onFileChange}
                onFileClick={downloadURI}
                onFileDelete={deleteValidFile}
                loadingFiles={loadingFiles}
                maxUploads={4}
                uploadEnabled={editableProposalStatus.includes(data?.status)}
              />
            )}
          </Box>
        </Grid>
      </Grid>
      <Box className={classes.proposalsContainer}>
        {/* Left Column */}
        <Box className={classes.leftColumn}>
          <Box className={classes.inputElement}>
            <InputLabel
              shrink
              htmlFor="location-input"
              className={classes.bootstrapFormLabel}
            >
              <span className={classes.requiredAsterisk}>*</span>
              {t('proposals.labels.location')}
            </InputLabel>
            {data?.workorder ? (
              <TextField
                value={data?.location?.name}
                variant="outlined"
                disabled
                classes={{ root: classes.inputRoot }}
              />
            ) : (
              <Autocomplete
                id="location-input"
                options={locationList}
                disableClearable
                disabled={editionDisabled || data?.workorder}
                value={
                  locationList.length
                    ? locationList.find(loc => loc.id === data?.location?.id) ||
                      null
                    : null
                }
                classes={{
                  listbox: classes.autocompleteListbox,
                  option: classes.autocompleteOption
                }}
                onChange={(event, value) =>
                  handleInputChange('location', value)
                }
                getOptionLabel={option =>
                  typeof option === 'object'
                    ? option.label +
                      ' - ' +
                      option.address +
                      ' - ' +
                      option.client_name
                    : ''
                }
                filterOptions={(options, state) => {
                  return options.filter(option =>
                    (
                      option.label +
                      ' - ' +
                      option.address +
                      ' - ' +
                      option.client_name
                    )
                      .toLowerCase()
                      .includes(state.inputValue.toLowerCase())
                  )
                }}
                renderInput={params => (
                  <TextField
                    {...params}
                    variant="outlined"
                    placeholder={'Type in the Location'}
                    className={classes.inputRoot}
                  />
                )}
              />
            )}
          </Box>
          <Box className={classes.inputElement}>
            <InputLabel
              shrink
              htmlFor="client-input"
              className={classes.bootstrapFormLabel}
            >
              {t('proposals.labels.client')}
            </InputLabel>

            <TextField
              value={data?.client?.name}
              variant="outlined"
              disabled
              placeholder="Select Location first"
              classes={{ root: classes.inputRoot }}
            />
          </Box>

          <Box className={classes.subContainer}>
            <Box className={classes.inputElement}>
              <InputLabel
                shrink
                htmlFor="priority-input"
                className={classes.bootstrapFormLabel}
              >
                {t('proposals.labels.priority')}
              </InputLabel>
              <Select
                id="priority-input"
                fullWidth
                variant="outlined"
                disabled={editionDisabled}
                className={classes.select}
                classes={{
                  icon: classes.icon
                }}
                value={selectedPriority}
                displayEmpty
                renderValue={selected => {
                  if (!selected) {
                    return (
                      <span className={classes.proposal_placeholder}>
                        {t('proposals.placeholder.select_priority')}
                      </span>
                    )
                  }
                  return selected
                }}
                onChange={event => {
                  setSelectedPriority(event.target.value)
                  handleInputChange('priority', event.target.value)
                }}
                MenuProps={{
                  anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: 'left'
                  },
                  transformOrigin: {
                    vertical: 'top',
                    horizontal: 'left'
                  },
                  getContentAnchorEl: null,
                  PaperProps: {
                    style: {
                      minWidth: 0,
                      width: 'auto',
                      maxWidth: '100%'
                    }
                  }
                }}
              >
                <MenuItem
                  value=""
                  disabled
                  classes={{ root: classes.innerSelectItem }}
                >
                  {t('proposals.placeholder.select_priority')}
                </MenuItem>
                {priorityList?.map(option => (
                  <MenuItem
                    key={option.id}
                    value={option.label}
                    classes={{ root: classes.innerSelectItem }}
                  >
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </Box>

            <Box className={classes.inputElement}>
              <InputLabel
                shrink
                htmlFor="trade-input"
                className={classes.bootstrapFormLabel}
              >
                {t('proposals.labels.trade')}
              </InputLabel>
              {data?.workorder ? (
                <TextField
                  value={data?.trade}
                  variant="outlined"
                  disabled
                  classes={{ root: classes.inputRoot }}
                />
              ) : (
                <Select
                  id="trade-input"
                  fullWidth
                  variant="outlined"
                  disabled={editionDisabled || data?.workorder}
                  className={classes.select}
                  classes={{
                    icon: classes.icon
                  }}
                  value={selectedTrade}
                  onChange={event => {
                    setSelectedTrade(event.target.value)
                    handleInputChange('trade', event.target.value)
                  }}
                  MenuProps={{
                    anchorOrigin: {
                      vertical: 'bottom',
                      horizontal: 'left'
                    },
                    transformOrigin: {
                      vertical: 'top',
                      horizontal: 'left'
                    },
                    getContentAnchorEl: null,
                    PaperProps: {
                      style: {
                        minWidth: 0,
                        width: 'auto',
                        maxWidth: '100%'
                      }
                    }
                  }}
                >
                  <MenuItem
                    value=""
                    disabled
                    classes={{ root: classes.innerSelectItem }}
                  >
                    {t('proposals.placeholder.select_trade')}
                  </MenuItem>
                  {tradeList?.map(option => (
                    <MenuItem
                      key={option.id}
                      value={option.label}
                      classes={{ root: classes.innerSelectItem }}
                    >
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              )}
            </Box>
          </Box>
        </Box>

        {/* Vertical Divider */}
        <Divider orientation="vertical" flexItem />

        {/* Right Column */}
        <Box className={classes.rightColumn}>
          <Box className={classes.subContainer}>
            <Box className={classes.inputElement}>
              <InputLabel
                shrink
                htmlFor="approver-input"
                className={classes.bootstrapFormLabel}
              >
                <span className={classes.subContainer}>
                  {t('proposals.labels.approver')}
                  <Tooltip
                    title={t('proposals.tooltips.approver')}
                    arrow
                    placement="top-end"
                    classes={{ tooltip: classes.tooltip }}
                  >
                    <IconButton
                      size="medium"
                      classes={{ root: classes.noPaddingIconButton }}
                    >
                      <FontAwesomeIcon
                        icon="fa-regular fa-circle-info"
                        fontSize="large"
                      />
                    </IconButton>
                  </Tooltip>
                </span>
              </InputLabel>
              <Autocomplete
                id="approver-input"
                options={sfUserList}
                disableClearable
                disabled={editionDisabled}
                popupIcon={null}
                classes={{
                  listbox: classes.autocompleteListbox,
                  option: classes.autocompleteOption
                }}
                value={
                  sfUserList.length
                    ? sfUserList.find(user => user.id === data?.approved_by) ||
                      null
                    : null
                }
                onChange={(event, value) =>
                  handleInputChange('approved_by', value.id)
                }
                renderInput={params => (
                  <TextField
                    {...params}
                    variant="outlined"
                    className={classes.inputRoot}
                  />
                )}
              />
            </Box>
            <Box className={classes.inputElement}>
              <InputLabel
                shrink
                htmlFor="user-input"
                className={classes.bootstrapFormLabel}
              >
                {t('proposals.labels.contact')}
              </InputLabel>
              <Autocomplete
                id="user-input"
                options={userList}
                disableClearable
                disabled={editionDisabled}
                popupIcon={null}
                classes={{
                  listbox: classes.autocompleteListbox,
                  option: classes.autocompleteOption
                }}
                value={
                  userList.length
                    ? userList.find(user => user.id === data?.created_by) ||
                      null
                    : null
                }
                onChange={(event, value) =>
                  handleInputChange('created_by', value.id)
                }
                renderInput={params => (
                  <TextField
                    {...params}
                    variant="outlined"
                    className={classes.inputRoot}
                  />
                )}
              />
            </Box>
          </Box>
          <Box className={classes.inputElement}>
            {data?.workorder?.customer_po && (
              <div>
                <InputLabel
                  shrink
                  htmlFor="wonotes-input"
                  className={classes.bootstrapFormLabel}
                >
                  {t('proposals.labels.wo_notes') + ': '}
                </InputLabel>
                <TextField
                  id="wonotes-input"
                  value={data?.workorder?.general_notes}
                  variant="outlined"
                  disabled
                  placeholder="This information comes from the linked Work Order"
                  className={classes.workorderNotes}
                  multiline={true}
                  maxRows={3}
                  minRows={3}
                />
              </div>
            )}
          </Box>
        </Box>
      </Box>
      <Box className={classes.descriptionContainer}>
        <CustomTextEditor
          description={description}
          editionDisabled={editionDisabled}
          handleDescriptionChange={handleDescriptionChange}
        />
      </Box>
    </Box>
  )
}

export const ProposalHeaderComponent = props => {
  const {
    data,
    handleChangeData,
    editionDisabled,
    locationList,
    userList,
    priorityList,
    tradeList,
    proposalPrefix,
    proposalNumberId,
    attachments,
    setAttachments,
    setData,
    sfUserList
  } = props
  return (
    <ProposalHeader
      data={data}
      handleChangeData={handleChangeData}
      editionDisabled={editionDisabled}
      locationList={locationList}
      userList={userList}
      priorityList={priorityList}
      tradeList={tradeList}
      proposalPrefix={proposalPrefix}
      proposalNumberId={proposalNumberId}
      attachments={attachments}
      setAttachments={setAttachments}
      setData={setData}
      sfUserList={sfUserList}
    />
  )
}
